"use client";

import cx from "classnames";
import OddsBtn, { useRedirectConfirmationModal } from "@/components/oddsBtn";
import TeamPlayerAvatar from "@/components/teamPlayerAvatar";
import { SPORTSBOOK_URL } from "@/constants/amplitude";
import { NFL } from "@/constants/sports";
import RedirectToSportsbook from "@/events/Modals/RedirectToSportsbookModal";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import type { Maybe, OddsSelection } from "@/graphql/gql/graphql";
import styles from "./Selection.module.scss";

type SelectionProps = {
  primaryOdds: Maybe<OddsSelection>;
  secondaryOdds?: Maybe<OddsSelection>;
};

const Selection = ({ primaryOdds, secondaryOdds }: SelectionProps) => {
  const classButton = cx(styles.oddsBtn, {
    [styles.under]: secondaryOdds !== undefined,
  });
  const classContainer = cx(styles.container, {
    [styles.lineBet]: !secondaryOdds,
    [styles.totalBet]: secondaryOdds,
  });

  // handle player and team info
  const playerName = primaryOdds?.player?.name ?? "";
  const jerseyImage = primaryOdds?.player?.teamInfo?.teamJersey?.svg ?? "";
  const teamLogoImage = primaryOdds?.player?.teamInfo?.logo?.svg ?? "";
  const teamName = primaryOdds?.player?.teamInfo?.name ?? "";

  // handle primary odds (or the over odds if it's a total bet selection)
  const odds = primaryOdds?.odds ?? "";
  const handicap = primaryOdds?.formattedHandicap ?? "";
  const oddsUrl = primaryOdds?.url ?? SPORTSBOOK_URL;

  // handle under odds (if it's a total bet selection)
  const underOdds = secondaryOdds?.odds ?? "";
  const underHandicap = secondaryOdds?.formattedHandicap ?? "";
  const underUrl = secondaryOdds?.url ?? SPORTSBOOK_URL;

  // handle redirect modal to sportsbook
  const { eventName } = useHeadToHeadContext();
  const { onOddsClick, redirectToSportsbookProps } =
    useRedirectConfirmationModal(NFL, eventName, "head_to_head");

  return (
    <div className={classContainer}>
      <div className={styles.avatarAndName}>
        <TeamPlayerAvatar
          primaryImgName={playerName}
          primaryImgUrl={jerseyImage}
          primaryImgSize={48}
          secondaryImgName={`${teamName} Logo`}
          secondaryImgUrl={teamLogoImage}
          secondaryImgSize={24}
        />
        <div className={styles.playerName}>{playerName}</div>
      </div>
      <div className={styles.oddsContainer}>
        <RedirectToSportsbook {...redirectToSportsbookProps} />
        <OddsBtn
          classButton={classButton}
          handicap={handicap}
          odds={odds}
          onClick={() => onOddsClick(oddsUrl)}
        />
        {secondaryOdds ? (
          <OddsBtn
            classButton={classButton}
            handicap={underHandicap}
            odds={underOdds}
            onClick={() => onOddsClick(underUrl)}
          />
        ) : null}
      </div>
    </div>
  );
};

export default Selection;
